// ── Start-screen music ───────────────────────────────────────────────────────
// A single looping track played behind the start screen, independent of the
// per-scenario AudioManager (which is torn down and rebuilt on every scenario).
// Backed by a plain HTMLAudioElement so it survives those teardowns and needs no
// Web Audio graph of its own.
//
// Effective volume = base (config) × music × master (both user settings) ×
// fade (0..1). The fade is driven by a self-contained requestAnimationFrame loop
// in real time, so it's decoupled from the (pausable) sim clock.

export default class MusicPlayer {
  // `loop: false` makes it a play-once cue (the first-run intro's song): no
  // native loop and no restart-on-ended safety net.
  constructor(url, { volume = 1, masterVolume = 1, musicVolume = 1, loop = true } = {}) {
    this._audio = new Audio(url);
    this._audio.loop = loop;
    this._audio.preload = 'auto';

    // Safety net for looping. HTMLMediaElement.loop is normally honored and
    // suppresses the 'ended' event entirely. In some browser states the native
    // loop is silently skipped after the element has been paused/seeked and
    // resumed — e.g. when the victory theme started via playFrom() carries over
    // into the start screen. If 'ended' ever fires, restart from the top so the
    // track keeps looping (the fade envelope is preserved).
    this._audio.addEventListener('ended', () => {
      if (!loop) return;
      try { this._audio.currentTime = 0; } catch { /* not yet seekable */ }
      const p = this._audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    });

    this._baseVolume = volume;
    this._masterVolume = masterVolume;
    this._musicVolume = musicVolume;
    this._fade = 1;            // 0..1 fade envelope
    this._fadeRAF = null;
    this._applyVolume();
  }

  _applyVolume() {
    // HTMLMediaElement.volume must stay within [0, 1].
    const v = this._baseVolume * this._musicVolume * this._masterVolume * this._fade;
    this._audio.volume = Math.max(0, Math.min(1, v));
  }

  // Start (or restart) the looping track from the beginning at full fade.
  play() {
    this._cancelFade();
    this._fade = 1;
    this._applyVolume();
    try { this._audio.currentTime = 0; } catch { /* not yet seekable */ }
    // Playback may be blocked until the first user gesture; the start screen is
    // only reached via a button click, so this resolves in practice. Swallow the
    // rejection either way.
    const p = this._audio.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  // Start the track from `startSeconds` in, easing the fade envelope up over
  // `fadeIn` seconds. A distinct cue from play() (which always starts at 0:00 at
  // full volume): used for the in-scenario victory theme. Looping still wraps
  // back to 0:00 — only the initial entry point is offset.
  playFrom(startSeconds = 0, fadeIn = 0) {
    this._cancelFade();
    try { this._audio.currentTime = startSeconds; } catch { /* not yet seekable */ }
    this._fade = fadeIn > 0 ? 0 : 1;
    this._applyVolume();
    // Playback may be blocked until the first user gesture; by the time a mission
    // completes the player has long since clicked through the start screen, so
    // this resolves in practice. Swallow the rejection either way.
    const p = this._audio.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
    if (fadeIn > 0) this._fadeTo(1, fadeIn);
  }

  // Fade the track out over `duration` seconds, then pause and rewind it.
  fadeOutAndStop(duration = 0) {
    this._cancelFade();
    if (this._audio.paused) return;
    if (duration <= 0) { this._stop(); return; }
    this._fadeTo(0, duration, () => this._stop());
  }

  // Ease the fade envelope toward `target` (0..1) over `duration` seconds, then
  // run `onComplete`. Driven by a real-time rAF loop (decoupled from the pausable
  // sim clock). Cancels any in-flight fade first.
  _fadeTo(target, duration, onComplete = null) {
    this._cancelFade();
    if (duration <= 0) {
      this._fade = target;
      this._applyVolume();
      if (onComplete) onComplete();
      return;
    }
    const startFade = this._fade;
    const startTime = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - startTime) / (duration * 1000));
      this._fade = startFade + (target - startFade) * t;
      this._applyVolume();
      if (t < 1) {
        this._fadeRAF = requestAnimationFrame(step);
      } else {
        this._fadeRAF = null;
        if (onComplete) onComplete();
      }
    };
    this._fadeRAF = requestAnimationFrame(step);
  }

  // True while the track is actively sounding (not paused/stopped). Lets callers
  // continue an already-playing track instead of restarting it from 0:00.
  get isPlaying() {
    return !this._audio.paused;
  }

  // User master-volume change from the settings overlay.
  setMasterVolume(v) {
    this._masterVolume = Math.max(0, Math.min(1, v));
    this._applyVolume();
  }

  // User music-volume change from the settings overlay.
  setMusicVolume(v) {
    this._musicVolume = Math.max(0, Math.min(1, v));
    this._applyVolume();
  }

  _stop() {
    this._audio.pause();
    try { this._audio.currentTime = 0; } catch { /* not yet seekable */ }
    this._fade = 1;
    this._applyVolume();
  }

  _cancelFade() {
    if (this._fadeRAF !== null) {
      cancelAnimationFrame(this._fadeRAF);
      this._fadeRAF = null;
    }
  }

  dispose() {
    this._cancelFade();
    this._audio.pause();
    this._audio.src = '';
  }
}
